import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { adminApi } from './api'
import { ADMIN_TOKEN_KEY, ADMIN_USER_KEY, type AdminUser } from './types'
import {
  clearAuthSession,
  detectAuthPersist,
  getAuthItem,
  persistAuthSession,
  setAuthItem,
} from '../shared/authStorage'

type AdminAuthValue = {
  token: string | null
  admin: AdminUser | null
  isAuthenticated: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => void
}

const AdminAuthContext = createContext<AdminAuthValue | null>(null)

function readStoredAdmin(): AdminUser | null {
  try {
    const raw = getAuthItem(ADMIN_USER_KEY)
    if (!raw) return null
    return JSON.parse(raw) as AdminUser
  } catch {
    return null
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAuthItem(ADMIN_TOKEN_KEY))
  const [admin, setAdmin] = useState<AdminUser | null>(() => readStoredAdmin())

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    const res = await adminApi.login(email, password)
    const nextAdmin = res.admin || null
    persistAuthSession({
      tokenKey: ADMIN_TOKEN_KEY,
      userKey: ADMIN_USER_KEY,
      token: res.accessToken,
      userJson: nextAdmin ? JSON.stringify(nextAdmin) : null,
      rememberMe,
    })
    setToken(res.accessToken)
    setAdmin(nextAdmin)
  }, [])

  const logout = useCallback(() => {
    clearAuthSession(ADMIN_TOKEN_KEY, ADMIN_USER_KEY)
    setToken(null)
    setAdmin(null)
  }, [])

  const value = useMemo(
    () => ({
      token,
      admin,
      isAuthenticated: Boolean(token),
      login,
      logout,
    }),
    [token, admin, login, logout],
  )

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}

/** Keep user profile writes in the same store as the active token. */
export function touchAdminUser(admin: AdminUser | null) {
  const mode = detectAuthPersist(ADMIN_TOKEN_KEY)
  if (!admin) {
    clearAuthSession(ADMIN_TOKEN_KEY, ADMIN_USER_KEY)
    return
  }
  setAuthItem(ADMIN_USER_KEY, JSON.stringify(admin), mode)
}
